import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { createApi } from "../lib/api";
import { useWedding } from "./wedding-context";

interface GuestInfo {
  id?: string;
  name: string | null;
  phone: string;
}

interface UnreadCounts {
  gallery: number;
  activity: number;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  guest: GuestInfo | null;
  unread: UnreadCounts;
  /** Zero out ONE scope's server-reported baseline after it's been marked seen
   *  — without this, `unread.*` stays at its session-start value forever and
   *  the gallery banner / notification dot can never fully clear. */
  clearUnread: (scope: "gallery" | "activity") => void;
  requestCode: (phone: string, turnstileToken: string) => Promise<{ authed?: boolean; verificationSid?: string }>;
  verifyCode: (phone: string, code: string, verificationSid?: string) => Promise<void>;
  /**
   * Oturumu başka bir endpoint açtıysa (OTP kapalıyken `/auth/register`)
   * durumu buradan işaretle — tam sayfa reload'a gerek kalmasın. Reload,
   * premium join temalarında loader + intro animasyonunu baştan oynatıyordu.
   */
  markAuthenticated: (guest: GuestInfo) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { slug } = useWedding();
  const weddingApi = useMemo(() => createApi(slug), [slug]);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [guest, setGuest] = useState<GuestInfo | null>(null);
  const [unread, setUnread] = useState<UnreadCounts>({ gallery: 0, activity: 0 });

  // Check if session is valid on mount
  useEffect(() => {
    weddingApi
      .get<{ guest?: GuestInfo }>("/auth/me")
      .then((res) => {
        setIsAuthenticated(true);
        const raw = res as unknown as { ok: boolean; guest?: GuestInfo; unread?: UnreadCounts };
        if (raw.guest) setGuest(raw.guest);
        if (raw.unread) setUnread(raw.unread);
      })
      .catch(() => setIsAuthenticated(false))
      .finally(() => setIsLoading(false));
  }, [weddingApi]);

  // Global 401/403 interceptor — any API call returning session-expired or banned
  // while the guest appears authenticated triggers an immediate state reset so they
  // land on the login page on the next render cycle
  useEffect(() => {
    function handleUnauthenticated() {
      setIsAuthenticated(prev => {
        if (prev) {
          setGuest(null);
          return false;
        }
        return prev;
      });
    }
    window.addEventListener("guest:unauthenticated", handleUnauthenticated);
    return () => window.removeEventListener("guest:unauthenticated", handleUnauthenticated);
  }, []);

  const requestCode = useCallback(async (phone: string, turnstileToken: string): Promise<{ authed?: boolean; verificationSid?: string }> => {
    const res = await weddingApi.post<{ authed?: boolean; verificationSid?: string; guest?: { name: string | null; phone: string } }>(
      "/auth/request-code",
      { phone, turnstileToken }
    );
    const raw = res as unknown as { ok: boolean; authed?: boolean; verificationSid?: string; guest?: { name: string | null; phone: string } };
    if (raw.authed && raw.guest) {
      setGuest(raw.guest);
      setIsAuthenticated(true);
    }
    return { authed: raw.authed, verificationSid: raw.verificationSid };
  }, [weddingApi]);

  const verifyCode = useCallback(async (phone: string, code: string, verificationSid?: string) => {
    const res = await weddingApi.post("/auth/verify-code", { phone, code, ...(verificationSid ? { verificationSid } : {}) });
    const raw = res as unknown as { ok: boolean; guest?: GuestInfo };
    if (raw.guest) setGuest(raw.guest);
    setIsAuthenticated(true);
  }, [weddingApi]);

  const markAuthenticated = useCallback((g: GuestInfo) => {
    setGuest(g);
    setIsAuthenticated(true);
  }, []);

  const clearUnread = useCallback((scope: "gallery" | "activity") => {
    setUnread((prev) => ({ ...prev, [scope]: 0 }));
  }, []);

  const logout = useCallback(async () => {
    await weddingApi.post("/auth/logout");
    setIsAuthenticated(false);
    setGuest(null);
  }, [weddingApi]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, guest, unread, clearUnread, requestCode, verifyCode, markAuthenticated, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
